import React from "react";
import { UsageGranularity } from "../../../api/admin/types";
import { GRANULARITY_OPTIONS, MetricsMode, MetricsTab } from "./constants";

interface ToolbarProps {
  // Tab is live (purely UI, no query params), so it bypasses the draft/apply
  // dance.
  tab: MetricsTab;
  setTab: (t: MetricsTab) => void;

  // Mode change resets dates to a sensible default and is auto-applied so the
  // user immediately sees something for the new mode.
  mode: MetricsMode;
  onModeChange: (m: MetricsMode) => void;

  // Draft (form) state - changes here are NOT fetched until Apply is clicked.
  draftGranularity: UsageGranularity;
  draftStartA: string;
  draftEndA: string;
  draftStartB: string;
  draftEndB: string;
  draftIncludeTestAccounts: boolean;
  setDraftGranularity: (g: UsageGranularity) => void;
  setDraftStartA: (v: string) => void;
  setDraftEndA: (v: string) => void;
  setDraftStartB: (v: string) => void;
  setDraftEndB: (v: string) => void;
  setDraftIncludeTestAccounts: (v: boolean) => void;

  hasPendingChanges: boolean;
  isFetching: boolean;
  onApply: () => void;

  // Epic visibility is purely a display toggle (no refetch needed), so it
  // bypasses the draft/apply dance like `tab` does.
  showEpic: boolean;
  setShowEpic: (v: boolean) => void;
}

const segmentBox: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  background: "#f0f0f0",
  borderRadius: 6,
  padding: 3,
};

function segmentBtn(active: boolean): React.CSSProperties {
  return {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    background: active ? "#3949ab" : "transparent",
    color: active ? "#fff" : "#555",
    transition: "all 0.15s ease",
  };
}

const fieldLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: 4,
};

const dtInput: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 4,
  border: "1px solid #ccc",
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
};

const InlineSpinner: React.FC = () => (
  <span
    aria-label="loading"
    style={{
      display: "inline-block",
      width: 14,
      height: 14,
      border: "2px solid rgba(255,255,255,0.4)",
      borderTopColor: "#fff",
      borderRadius: "50%",
      animation: "metrics-toolbar-spin 0.8s linear infinite",
    }}
  />
);

const Toolbar: React.FC<ToolbarProps> = ({
  tab,
  setTab,
  mode,
  onModeChange,
  draftGranularity,
  draftStartA,
  draftEndA,
  draftStartB,
  draftEndB,
  draftIncludeTestAccounts,
  setDraftGranularity,
  setDraftStartA,
  setDraftEndA,
  setDraftStartB,
  setDraftEndB,
  setDraftIncludeTestAccounts,
  hasPendingChanges,
  isFetching,
  onApply,
  showEpic,
  setShowEpic,
}) => {
  const applyDisabled = !hasPendingChanges && !isFetching;

  return (
    <div
      style={{
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      {/* keyframes for the spinner; injected once per Toolbar instance */}
      <style>{`@keyframes metrics-toolbar-spin{to{transform:rotate(360deg)}}`}</style>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
        <div>
          <div style={fieldLabel}>Mode</div>
          <div style={segmentBox}>
            <button onClick={() => onModeChange("single")} style={segmentBtn(mode === "single")}>
              Single period
            </button>
            <button onClick={() => onModeChange("compare")} style={segmentBtn(mode === "compare")}>
              Compare
            </button>
          </div>
        </div>
        <div>
          <div style={fieldLabel}>View</div>
          <div style={segmentBox}>
            <button onClick={() => setTab("agency")} style={segmentBtn(tab === "agency")}>
              Agency totals
            </button>
            <button onClick={() => setTab("user")} style={segmentBtn(tab === "user")}>
              User totals
            </button>
            <button onClick={() => setTab("role")} style={segmentBtn(tab === "role")}>
              Roles
            </button>
            <button onClick={() => setTab("policy")} style={segmentBtn(tab === "policy")}>
              Policy insights
            </button>
          </div>
        </div>
        <div>
          <div style={fieldLabel}>Granularity</div>
          <div style={segmentBox}>
            {GRANULARITY_OPTIONS.map((g) => (
              <button
                key={g.value}
                onClick={() => setDraftGranularity(g.value)}
                style={segmentBtn(draftGranularity === g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 16,
            marginLeft: "auto",
            alignItems: "center",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#374151",
            }}
          >
            <input
              type="checkbox"
              checked={showEpic}
              onChange={(e) => setShowEpic(e.target.checked)}
            />
            Show Epic runs
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#374151",
            }}
          >
            <input
              type="checkbox"
              checked={draftIncludeTestAccounts}
              onChange={(e) => setDraftIncludeTestAccounts(e.target.checked)}
            />
            Include test accounts
          </label>
        </div>
      </div>

      {mode === "single" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <div>
            <div style={fieldLabel}>Start</div>
            <input
              type="datetime-local"
              value={draftStartA}
              onChange={(e) => setDraftStartA(e.target.value)}
              style={dtInput}
            />
          </div>
          <div>
            <div style={fieldLabel}>End</div>
            <input
              type="datetime-local"
              value={draftEndA}
              onChange={(e) => setDraftEndA(e.target.value)}
              style={dtInput}
            />
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <div>
            <div style={fieldLabel}>Period A start</div>
            <input
              type="datetime-local"
              value={draftStartA}
              onChange={(e) => setDraftStartA(e.target.value)}
              style={dtInput}
            />
          </div>
          <div>
            <div style={fieldLabel}>Period A end</div>
            <input
              type="datetime-local"
              value={draftEndA}
              onChange={(e) => setDraftEndA(e.target.value)}
              style={dtInput}
            />
          </div>
          <div>
            <div style={fieldLabel}>Period B start</div>
            <input
              type="datetime-local"
              value={draftStartB}
              onChange={(e) => setDraftStartB(e.target.value)}
              style={dtInput}
            />
          </div>
          <div>
            <div style={fieldLabel}>Period B end</div>
            <input
              type="datetime-local"
              value={draftEndB}
              onChange={(e) => setDraftEndB(e.target.value)}
              style={dtInput}
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
        {hasPendingChanges && !isFetching ? (
          <span style={{ fontSize: 12, color: "#92400e" }}>Unapplied changes</span>
        ) : null}
        {isFetching ? (
          <span style={{ fontSize: 12, color: "#1f2937" }}>Loading data…</span>
        ) : null}
        <button
          onClick={onApply}
          disabled={applyDisabled}
          style={{
            padding: "10px 20px",
            borderRadius: 6,
            border: "none",
            background: applyDisabled ? "#9ca3af" : "#3949ab",
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            cursor: applyDisabled ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            minWidth: 100,
            justifyContent: "center",
          }}
        >
          {isFetching ? <InlineSpinner /> : null}
          {isFetching ? "Loading…" : "Apply"}
        </button>
      </div>
    </div>
  );
};

export default Toolbar;
